/**
 * formatMessageDateTime — a message timestamp with its TIME OF DAY (055 FR-001).
 *
 * The customer's decision (2026-09-29) was that a date alone is not enough: a
 * member following a multi-day exchange needs to see how long a reply took.
 *
 * Deliberately built on `Intl` + the project's `localeTag`, NOT hand-rolled.
 * The prototype composes the Finnish string by hand with a literal `klo`:
 *
 *     `${d.getDate()}.${d.getMonth()+1}.${d.getFullYear()} klo ${timeFmt.format(d)}`
 *
 * which is fine in a Finnish-only demo and wrong here — Oma+ serves fi/en/sv
 * (FR-022). `Intl` with `{ dateStyle: 'short', timeStyle: 'short' }` produces
 * the BYTE-IDENTICAL Finnish output (`27.9.2026 klo 19.02`) while giving correct
 * Swedish and English, so there is nothing to gain by copying the prototype here
 * and two languages to lose.
 *
 * TIME ZONE: the device's, not Finland's (owner decision 2026-09-30, D-232(e)).
 * `Intl` resolves the browser's zone, so a member abroad sees a reply sent at
 * 15.02 Finnish time as their own 13.02. That is the messaging-app convention
 * and keeps "two hours ago" meaning two hours ago wherever they are; pinning to
 * Europe/Helsinki would show a clock they are not living by. Revisit only if
 * handling deadlines ever need to be read off these timestamps.
 *
 * Rendered forms:
 *   fi-FI  →  27.9.2026 klo 19.02
 *   en-GB  →  27/09/2026, 19:02
 *   sv-FI  →  27.9.2026 19.02
 */

import type { LanguageCode } from '../i18n/LanguagePort.js';
import { localeTag } from '../i18n/localeTag.js';

/**
 * Format an IS timestamp for display on a message.
 *
 * Returns `''` for anything unparseable rather than surfacing the failure. This
 * guard is load-bearing: `Intl.DateTimeFormat.format()` THROWS a `RangeError` on
 * an invalid date — it does not quietly yield "Invalid Date" — so removing the
 * check turns one bad upstream timestamp into a blank conversation.
 */
export function formatMessageDateTime(value: string, active: LanguageCode): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat(localeTag(active), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(d);
}
